/**
 * Shared vocabulary: time, units, orders, postures, terrain, weather words, techs, option lists.
 * English is the source of truth for keys and parameters (see ../types.ts).
 */
export const common = {
  // ---- generic controls
  'ui.back': 'Back',
  'ui.close': 'Close',
  'ui.closeEsc': 'Close (Esc)',
  'ui.dismiss': 'Dismiss',
  'ui.reset': 'Reset',
  'ui.random': 'Random',
  'ui.fixed': 'Fixed',
  'ui.none': 'None',
  'ui.test': 'Test',
  'ui.showOnMap': 'Show on map',
  'ui.showOnTheMap': 'Show on the map',
  'ui.language': 'Language',

  // ---- time
  'time.day': 'Day {n}',
  'time.dayOf': 'Day {n} / {of}',
  'time.date': 'Day {day} · {hour}',
  'time.stamp': 'D{day} {clock}',
  'time.dayShort': 'D{n}',
  'time.hhour': 'H {hour} D{day}',
  'time.hhourTag': 'H {hour}',
  'time.now': 'now',
  'time.hours': '{h}h',
  'time.days': '{d}d',
  'time.daysHours': '{d}d {h}h',
  'time.inHours': 'in {h}h',
  'time.days.long': { one: '{n} day', other: '{n} days' },
  'time.temp': '{t}°C',

  // ---- unit types
  'unit.infantry': 'Infantry',
  'unit.motorized': 'Motorized',
  'unit.armor': 'Armor',
  'unit.artillery': 'Artillery',
  'unit.recon': 'Reconnaissance',
  'unit.hq': 'Headquarters',
  'unit.decoy': 'Decoy',
  'unit.blurb.infantry': 'Backbone of the line. Cheap, stubborn in defence, slow on the march.',
  'unit.blurb.motorized': 'Lorried infantry. Holds like foot soldiers, moves twice as fast.',
  'unit.blurb.armor': 'Breakthrough and exploitation. Hungry for supply, poor in close terrain.',
  'unit.blurb.artillery': 'Supports every friendly battle within two hexes.',
  'unit.blurb.recon': 'Sight 4. Identifies what it sees and outruns trouble.',
  'unit.blurb.hq': 'Orders leave from here. Telephone net covers its command radius.',
  'unit.blurb.decoy': 'Canvas and plywood. Passes for a division until the enemy comes close.',
  'unit.formation.infantry': 'Infantry formation',
  'unit.formation.motorized': 'Motorized formation',
  'unit.formation.armor': 'Armor formation',
  'unit.formation.artillery': 'Artillery formation',
  'unit.formation.recon': 'Reconnaissance formation',
  'unit.formation.hq': 'Headquarters formation',
  'unit.formation.decoy': 'Decoy formation',
  'unit.unidentifiedFormation': 'unidentified formation',
  'unit.unidentifiedContact': 'Unidentified contact',
  'unit.aFormation': 'a formation',
  'unit.anHq': 'a headquarters',
  'unit.echelon.division': 'XX',
  'unit.echelon.army': 'XXXX',
  'unit.symbol.hq': 'HQ',
  'unit.symbol.decoy': 'D',
  'unit.decoyStamp': 'D E C O Y',

  // ---- unit status
  'status.idle': 'Idle',
  'status.moving': 'On the march',
  'status.attacking': 'Attacking',
  'status.defending': 'Defending',
  'status.retreating': 'Retreating',
  'status.entrenched': 'Dug in',
  'status.awaiting': 'Awaiting H-hour',
  'status.routed': 'Routed',

  // ---- postures & orders
  'posture.aggressive': 'Aggressive',
  'posture.balanced': 'Balanced',
  'posture.defensive': 'Defensive',
  'posture.aggressive.hint': 'Counter-attack weak neighbours on own initiative',
  'posture.balanced.hint': 'Hold ground',
  'posture.defensive.hint': 'Fall back before being shattered',
  'order.move': 'Move',
  'order.attack': 'Attack',
  'order.hold': 'Hold',
  'order.retreat': 'Retreat',
  'order.posture': 'Posture',
  'channel.telephone': 'telephone',
  'channel.courier': 'courier',
  'channel.radio': 'radio',

  // ---- experience
  'exp.0': 'Green',
  'exp.1': 'Trained',
  'exp.2': 'Seasoned',
  'exp.3': 'Veteran',
  'exp.4': 'Elite',

  // ---- compass: short points, and "from the W" wording
  'compass.E': 'E',
  'compass.NE': 'NE',
  'compass.N': 'N',
  'compass.NW': 'NW',
  'compass.W': 'W',
  'compass.SW': 'SW',
  'compass.S': 'S',
  'compass.SE': 'SE',
  'wind.from': 'wind from the {dir}',
  'wind.fromCap': 'Wind from the {dir}',

  // ---- terrain
  'terrain.0': 'Open sea',
  'terrain.1': 'Shallows',
  'terrain.2': 'Lake',
  'terrain.3': 'Plains',
  'terrain.4': 'Forest',
  'terrain.5': 'Hills',
  'terrain.6': 'Mountains',
  'terrain.7': 'Marsh',
  'terrain.8': 'Urban',
  'terrain.unknown': 'Unknown',

  // ---- places
  'place.front': 'the front',
  'place.atFront': 'at the front',
  'place.at': 'at {name}',
  'place.near': 'near {name}',
  'place.in': 'in {name}',
  'place.city': 'a city',
  'place.depot': 'the depot',
  'city.size.1': 'town',
  'city.size.2': 'city',
  'city.size.3': 'metropolis',
  'city.capital': 'capital',

  // ---- nations (fallbacks)
  'nation.enemy': 'enemy',
  'nation.theEnemy': 'the enemy',

  // ---- seasons
  'season.spring': 'Spring',
  'season.summer': 'Summer',
  'season.autumn': 'Autumn',
  'season.winter': 'Winter',
  'season.random': 'Random',
  'season.blurb.spring': 'Thaw and showers — lowlands in mud early, drying as the weeks warm.',
  'season.blurb.summer': 'Firm ground and long days — tank weather, broken by afternoon thunderstorms.',
  'season.blurb.autumn': 'Fronts, fog and the rasputitsa — the fields turn to mud, roads decide.',
  'season.blurb.winter': 'Frost and snow — frozen rivers and marshes bear, blizzards blind.',
  'season.blurb.random': 'The season is drawn with the theatre — scout the sky first.',

  // ---- weather words
  'sky.clear': 'Clear',
  'sky.fair': 'Fair',
  'sky.overcast': 'Overcast',
  'sky.rain': 'Rain',
  'sky.storm': 'Storm',
  'sky.snow': 'Snow',
  'sky.blizzard': 'Blizzard',
  'sky.fog': 'Fog',
  'ground.mud': 'Mud',
  'ground.snow': 'Snow cover',
  'ground.frozen': 'Frozen',
  'ground.wet': 'Wet',
  'ground.firm': 'firm',
  'outlook.clear': 'Dry',
  'outlook.rain': 'Rain',
  'outlook.storm': 'Storm',
  'outlook.snow': 'Snow',
  'outlook.blizzard': 'Blizzard',
  'outlook.fog': 'Fog',

  // ---- AI difficulty
  'difficulty.recruit': 'Recruit',
  'difficulty.veteran': 'Veteran',
  'difficulty.general': 'General',
  'difficulty.recruit.hint': 'Slow to react',
  'difficulty.veteran.hint': 'Competent staff work',
  'difficulty.general.hint': 'Fast and ruthless',

  // ---- map sizes
  'mapSize.small': 'Small',
  'mapSize.medium': 'Medium',
  'mapSize.large': 'Large',
  'mapSize.hexes': '{w} × {h} hexes',
  'mapSize.players.small': '2–3 factions',
  'mapSize.players.medium': '3–4 factions',
  'mapSize.players.large': '4–6 factions',

  // ---- game speed
  'speed.1': 'Deliberate — about one hour per second',
  'speed.2': 'Steady',
  'speed.3': 'Standard',
  'speed.4': 'Brisk',
  'speed.5': 'Blitz',
  'speed.label': 'Speed {v}',

  // ---- starting technology presets
  'techPreset.none': '1924 pattern',
  'techPreset.none.hint': 'Couriers and field telephone only — research the rest',
  'techPreset.modern': 'Modern war',
  'techPreset.modern.hint': 'Everyone starts with field radio and motorization',

  // ---- teams & limits
  'team.name': 'Team {name}',
  'dayLimit.days': '{n} days',

  // ---- research
  'branch.signals': 'Signals',
  'branch.intelligence': 'Intelligence',
  'branch.warfare': 'Warfare',
  'branch.logistics': 'Logistics',
  'tech.field_telephone.name': 'Field Telephone Net',
  'tech.field_telephone.desc': 'Command radius +2 hexes around every HQ.',
  'tech.motor_couriers.name': 'Motorcycle Couriers',
  'tech.motor_couriers.desc': 'Couriers travel ~75% faster.',
  'tech.radio.name': 'Field Radio',
  'tech.radio.desc': 'Orders may be sent by radio: near-instant, but enemy codebreakers can read them.',
  'tech.encryption.name': 'Rotor Cipher',
  'tech.encryption.desc': 'Enemy decryption chance on your radio traffic reduced by 60%.',
  'tech.codebreaking.name': 'Cryptanalysis Bureau',
  'tech.codebreaking.desc': 'Chance to decrypt enemy radio orders.',
  'tech.codebreaking2.name': 'Bombe Machines',
  'tech.codebreaking2.desc': 'Greatly improved decryption of enemy radio orders.',
  'tech.aerial_recon.name': 'Aerial Reconnaissance',
  'tech.aerial_recon.desc':
    'Recon units +2 sight; daily photo sweeps reveal enemy positions near your front.',
  'tech.deception.name': 'Deception Units',
  'tech.deception.desc': 'Build decoy divisions that pose as real formations until assessed at close range.',
  'tech.motorization.name': 'Motorization',
  'tech.motorization.desc': 'Unlock motorized infantry divisions.',
  'tech.armor2.name': 'Medium Tanks',
  'tech.armor2.desc': 'Armor attack +25%.',
  'tech.heavy_artillery.name': 'Heavy Artillery',
  'tech.heavy_artillery.desc': 'Artillery support range +1 and attack +20%.',
  'tech.combined_arms.name': 'Combined Arms Doctrine',
  'tech.combined_arms.desc': '+20% attack when a battle includes armor and infantry on the attacking side.',
  'tech.defense_in_depth.name': 'Defence in Depth',
  'tech.defense_in_depth.desc': 'Entrench twice as fast; +15% defence when entrenched.',
  'tech.railways.name': 'Military Railways',
  'tech.railways.desc': 'Supply range +40% along rails and roads.',
  'tech.field_hospitals.name': 'Field Hospitals',
  'tech.field_hospitals.desc': 'Units recover strength twice as fast and use 30% less manpower.',

  // ---- operation codenames (the dice in the orders footer)
  'op.prefix': 'Operation {name}',
  'op.names':
    'Anvil|Harrow|Kestrel|Lantern|Sickle|Tinder|Quarry|Bellows|Gauntlet|Cinder|Weir|Thistle|Hammerfall|Nightjar|Iron Gate|Millrace',
} as const;
